import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { runLanguageMigration } from './i18n/languageMigration';

// Run one-time migration BEFORE reading stored language
// This converts legacy 'en' users to 'nl' (NL-first strategy)
runLanguageMigration();

// NL-First Strategy: No browser detection, always default to Dutch
const getStoredLanguage = (): string => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('i18nextLng') || 'nl';
  }
  return 'nl';
};

// Import NL translations
import nlCommon from '@/locales/nl/common.json';
import nlAuth from '@/locales/nl/auth.json';
import nlSkills from '@/locales/nl/skills.json';
import nlProducts from '@/locales/nl/products.json';
import nlDashboard from '@/locales/nl/dashboard.json';
import nlAdmin from '@/locales/nl/admin.json';
import nlPricing from '@/locales/nl/pricing.json';
import nlFaq from '@/locales/nl/faq.json';
import nlLegal from '@/locales/nl/legal.json';
import nlHomepage from '@/locales/nl/homepage.json';
import nlHq from '@/locales/nl/hq.json';
import nlFunnels from '@/locales/nl/funnels.json';
import nlWebsite from '@/locales/nl/website.json';
import nlLanding from '@/locales/nl/landing.json';

// Import EN translations
import enCommon from '@/locales/en/common.json';
import enAuth from '@/locales/en/auth.json';
import enSkills from '@/locales/en/skills.json';
import enProducts from '@/locales/en/products.json';
import enDashboard from '@/locales/en/dashboard.json';
import enAdmin from '@/locales/en/admin.json';
import enPricing from '@/locales/en/pricing.json';
import enFaq from '@/locales/en/faq.json';
import enLegal from '@/locales/en/legal.json';
import enHomepage from '@/locales/en/homepage.json';
import enHq from '@/locales/en/hq.json';
import enFunnels from '@/locales/en/funnels.json';
import enWebsite from '@/locales/en/website.json';
import enLanding from '@/locales/en/landing.json';

// Import DE translations
import deCommon from '@/locales/de/common.json';
import deAuth from '@/locales/de/auth.json';
import deSkills from '@/locales/de/skills.json';
import deProducts from '@/locales/de/products.json';
import deDashboard from '@/locales/de/dashboard.json';
import deAdmin from '@/locales/de/admin.json';
import dePricing from '@/locales/de/pricing.json';
import deFaq from '@/locales/de/faq.json';
import deLegal from '@/locales/de/legal.json';
import deHq from '@/locales/de/hq.json';
import deFunnels from '@/locales/de/funnels.json';

// Import FR translations
import frCommon from '@/locales/fr/common.json';
import frAuth from '@/locales/fr/auth.json';
import frSkills from '@/locales/fr/skills.json';
import frProducts from '@/locales/fr/products.json';
import frDashboard from '@/locales/fr/dashboard.json';
import frAdmin from '@/locales/fr/admin.json';
import frPricing from '@/locales/fr/pricing.json';
import frFaq from '@/locales/fr/faq.json';
import frLegal from '@/locales/fr/legal.json';
import frHq from '@/locales/fr/hq.json';
import frFunnels from '@/locales/fr/funnels.json';

// Import ES translations
import esCommon from '@/locales/es/common.json';
import esAuth from '@/locales/es/auth.json';
import esSkills from '@/locales/es/skills.json';
import esProducts from '@/locales/es/products.json';
import esDashboard from '@/locales/es/dashboard.json';
import esAdmin from '@/locales/es/admin.json';
import esPricing from '@/locales/es/pricing.json';
import esFaq from '@/locales/es/faq.json';
import esLegal from '@/locales/es/legal.json';
import esHq from '@/locales/es/hq.json';
import esFunnels from '@/locales/es/funnels.json';

// Import PT translations
import ptCommon from '@/locales/pt/common.json';
import ptAuth from '@/locales/pt/auth.json';
import ptSkills from '@/locales/pt/skills.json';
import ptProducts from '@/locales/pt/products.json';
import ptDashboard from '@/locales/pt/dashboard.json';
import ptAdmin from '@/locales/pt/admin.json';
import ptPricing from '@/locales/pt/pricing.json';
import ptFaq from '@/locales/pt/faq.json';
import ptLegal from '@/locales/pt/legal.json';
import ptHq from '@/locales/pt/hq.json';
import ptFunnels from '@/locales/pt/funnels.json';

// Import IT translations
import itCommon from '@/locales/it/common.json';
import itAuth from '@/locales/it/auth.json';
import itSkills from '@/locales/it/skills.json';
import itProducts from '@/locales/it/products.json';
import itDashboard from '@/locales/it/dashboard.json';
import itAdmin from '@/locales/it/admin.json';
import itPricing from '@/locales/it/pricing.json';
import itFaq from '@/locales/it/faq.json';
import itLegal from '@/locales/it/legal.json';
import itHq from '@/locales/it/hq.json';
import itFunnels from '@/locales/it/funnels.json';

const resources = {
  nl: {
    common: nlCommon, auth: nlAuth, skills: nlSkills, products: nlProducts,
    dashboard: nlDashboard, admin: nlAdmin, pricing: nlPricing, faq: nlFaq,
    legal: nlLegal, homepage: nlHomepage, hq: nlHq, funnels: nlFunnels, website: nlWebsite, landing: nlLanding,
  },
  en: {
    common: enCommon, auth: enAuth, skills: enSkills, products: enProducts,
    dashboard: enDashboard, admin: enAdmin, pricing: enPricing, faq: enFaq,
    legal: enLegal, homepage: enHomepage, hq: enHq, funnels: enFunnels, website: enWebsite, landing: enLanding,
  },
  de: {
    common: deCommon, auth: deAuth, skills: deSkills, products: deProducts,
    dashboard: deDashboard, admin: deAdmin, pricing: dePricing, faq: deFaq,
    legal: deLegal, hq: deHq, funnels: deFunnels,
  },
  fr: {
    common: frCommon, auth: frAuth, skills: frSkills, products: frProducts,
    dashboard: frDashboard, admin: frAdmin, pricing: frPricing, faq: frFaq,
    legal: frLegal, hq: frHq, funnels: frFunnels,
  },
  es: {
    common: esCommon, auth: esAuth, skills: esSkills, products: esProducts,
    dashboard: esDashboard, admin: esAdmin, pricing: esPricing, faq: esFaq,
    legal: esLegal, hq: esHq, funnels: esFunnels,
  },
  pt: {
    common: ptCommon, auth: ptAuth, skills: ptSkills, products: ptProducts,
    dashboard: ptDashboard, admin: ptAdmin, pricing: ptPricing, faq: ptFaq,
    legal: ptLegal, hq: ptHq, funnels: ptFunnels,
  },
  it: {
    common: itCommon, auth: itAuth, skills: itSkills, products: itProducts,
    dashboard: itDashboard, admin: itAdmin, pricing: itPricing, faq: itFaq,
    legal: itLegal, hq: itHq, funnels: itFunnels,
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getStoredLanguage(), // NL-first: explicit language, no browser detection
    fallbackLng: {
      'default': ['nl', 'en'], // NL-first fallback chain
      'nl': ['en'],
      'en': ['nl'],
      'es': ['en', 'nl'],
      'de': ['en', 'nl'],
      'fr': ['en', 'nl'],
      'pt': ['en', 'nl'],
      'it': ['en', 'nl'],
    },
    supportedLngs: ['nl', 'en', 'es', 'de', 'fr', 'pt', 'it'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    defaultNS: 'common',
    ns: ['common', 'auth', 'skills', 'products', 'dashboard', 'admin', 'pricing', 'faq', 'legal', 'homepage', 'hq', 'funnels', 'website', 'landing'],
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;